import React from 'react'
import { observer } from 'mobx-react'
import { classNames, toPrice, useThrottle } from '@dt/fe-utils'
import { cartMobx, doComboMobx, doProductMobx, menuMobx, restaurantConfig } from 'restaurant'
import { modalLayer, toastLayer, handleEleScrollTo, showMsg } from '@utils'
import { ProductImage, Tags, Stepper, SoldoutFoldBar } from 'components'
import CashierController from 'controllers/cashierController'
import SelectProductModal from './SelectProductModal'
import Cart from './Cart'
import { filter, find, sortBy, uniqBy } from '@dt/fe-utils/dist/lodash'

interface IProps {
}

const AboardOptionalGroup: React.FC<IProps> = ({ }) => {
  const { doCombo, productGroups, doComboResult } = doComboMobx
  const isOpenSoldout = menuMobx.isOpenSoldout

  const handleScroll = (groupNo: string) => {
    const ele = document.querySelector(`#CFCG_${groupNo}`) as any
    handleEleScrollTo?.({
      selector: 'ion-content#comboFooterCartContent',
      x: ele.offsetLeft,
      duration: 10
    })
  }

  const openProductDetail = useThrottle((product) => {
    CashierController.openProductDetail(product, {
      type: 'combo',
      scaleNo: product.scaleNo,
      targetNo: product.groupNo,
    })
  }, 1000)

  const handleClick = (group, product, isCheck = false) => {
    handleScroll(group.no)
    const productGroup = doCombo.productGroups?.find(item => item.no === group.no)
    const cartProducts = productGroup?.productGroupDetails?.filter((item) => `${item.productNo}_${item.scaleNo}` === `${product.no}_${product.scaleNo}`)
    if (!isCheck) {
      if (cartProducts?.length > 1) {
        modalLayer(SelectProductModal, { type: 'show', products: cartProducts }, 'SelectProductModal').then((product: any) => {
          if (product) {
            doProductMobx.pick({ cartItem: product, groupNo: product.groupNo, addPrice: product.addPrice, isCombo: true, initAdd: true, salesCnt: product.cnt })
            openProductDetail(product)
          }
        }, () => { })
      } else {
        const target = cartProducts?.[0] ? { cartItem: cartProducts[0] } : { product }
        doProductMobx.pick({ ...target, groupNo: product.groupNo, addPrice: product.addPrice, isCombo: true, initAdd: true, salesCnt: product.cnt })
        openProductDetail(product)
      }
      return
    }
    if (cartProducts?.length === 1 && (!group.chooseType || group.selectMin === 1)) {
      doProductMobx.pick({ cartItem: cartProducts[0], groupNo: cartProducts[0].groupNo, addPrice: cartProducts[0].addPrice, isCombo: true, initAdd: true, salesCnt: product.cnt })
      openProductDetail(product)
    } else {
      doProductMobx.pick({ product, addPrice: product.addPrice, scaleNo: product.scaleNo, groupNo: group.no, isCombo: true, initAdd: true, salesCnt: product.cnt })
      const isAdd = doProductMobx.doProductResult.isReady && !product.modifierGroups?.length
      if (isAdd) {
        const { productNo, scaleNo, cnt } = product
        if (doProductMobx.checkInvalidByProductNo(productNo, scaleNo, 0, cnt)) {
          showMsg(i18n.t('productdetail.saleOutTips2'))
          return
        }
        doComboMobx.addToCombo({ ...doProductMobx.doProduct, cnt: product.cnt, salesCnt: product.salesCnt })
      } else {
        openProductDetail(product)
      }
    }
  }

  const handleCheck = (e, group, product) => {
    e.stopPropagation()
    const key = `${group.no}_${product.no}_${product.scaleNo}`
    if (doComboMobx.doComboResult.gpsNoToCntMap[key]) {
      doComboMobx.unselect(product)
      return
    }
    if (doComboMobx.doComboResult.groupNoToTypeCntMap[group.no] === group.selectMin) {
      if (group.selectMin > 1) {
        toastLayer.info(i18n.t('newCart.groupIsFull'))
        return
      } else {
        const productGroup = doCombo.productGroups?.find(item => item.no === group.no)
        const cartProduct = productGroup?.productGroupDetails[0]
        if (cartProduct) {
          doComboMobx.remove(cartProduct)
        }
      }
    }
    handleClick(group, product, true)
  }

  const handleChangeCnt = (e, isAdd, group, product) => {
    e.stopPropagation()
    const productGroup = doCombo.productGroups?.find(item => item.no === group.no)
    const cartProducts = productGroup?.productGroupDetails?.filter((item) => `${item.productNo}_${item.scaleNo}` === `${product.no}_${product.scaleNo}`)
    const key = `${group.no}_${product.no}_${product.scaleNo}`
    const cnt = doComboMobx.doComboResult.gpsNoToCntMap[key] / product.cnt
    const max = group.chooseType ? group.selectMin : 1
    if (isAdd) {
      if (cnt === max) {
        return
      }
      handleClick(group, product, true)
    } else {
      const uProducts = uniqBy(cartProducts, 'fkey')
      if (cnt === 1) {
        doComboMobx.unselect(product)
      } else if (uProducts.length === 1) {
        doComboMobx.remove(cartProducts[cartProducts.length - 1])
      } else {
        modalLayer(SelectProductModal, { type: 'remove', products: cartProducts }, 'SelectProductModal').then((products: any) => {
          products?.forEach(product => {
            doComboMobx.remove(product)
          })
        }, () => { })
      }
    }
  }

  return (
    <>
      <div className='combo-group-list'>
        {
          productGroups.map(group => {
            // 售罄子餐品数量
            const soldoutCnt = filter(group?.productGroupDetails, product => product.saleCnt === 0).length
            const showSoldout = menuMobx.openFoldSoldout === 1 && soldoutCnt > 0
            const soldoutKey = `${doCombo.no}_${group.no}`
            const productGroupDetails = !showSoldout || menuMobx.foldSoldoutMap?.[soldoutKey] === true ? sortBy(group?.productGroupDetails || [], (product) => product.saleCnt === 0 ? 1 : 0) : filter(group?.productGroupDetails, product => product.saleCnt !== 0)
            return (
              <div className='group-item' key={group.no}>
                <div className='group-title'>
                  {group.name}{productGroupDetails?.length > 1 && (group.selectMin > 1 || (!menuMobx.isOpenSoldout || ((productGroupDetails?.filter(item => item.saleCnt !== 0)).length) > 1)) && <span>{i18n.t('productdetail.canSelect', { count: group.selectMin })}</span>}
                </div>
                {
                  productGroupDetails?.map(product => {
                    const key = `${group.no}_${product.no}_${product.scaleNo}`
                    const max = group.chooseType ? (group.selectMin + doComboMobx.doComboResult.gpsNoToCntMap[key] - doComboMobx.doComboResult.groupNoToTypeCntMap[group.no]) : 1
                    const disableTogo = (cartMobx.info.eatType !== 'dinein' || doComboMobx.doCombo.isTogo) && !product.isCanTogo
                    const soldout = product.saleCnt === 0
                    // 售罄并且开启不展示
                    if (soldout && isOpenSoldout) return null
                    return (
                      <div className={classNames('product-item', { 'sold-out': soldout })} data-cypid={key} key={key} onClick={() => handleClick(group, product)}>
                        <div className='item-detail'>
                          <ProductImage color={!product.pic && restaurantConfig.displayMode === 'waterfall' ? product.productNo : ''} width={40} imgSrc={product.pic} />
                          <div className=''>
                            <strong>{product.name}</strong>
                            <Tags datas={[...(product.tags || []), ...(product.allergys || [])]} disableTogo={cartMobx.info.eatType !== 'dinein' && !product.isCanTogo} />
                            {product.intro ? <div className='product-intro' dangerouslySetInnerHTML={{ __html: product.intro }}></div> : ''}
                            <div className='product-option'>
                              {product.scales[0] ? product.scales[0].name : ''}
                            </div>
                          </div>
                        </div>
                        <div className='item-operate'>
                          {
                            !soldout && (
                              <>
                                {doComboMobx.doComboResult.gpsNoToCntMap[key] ? (
                                  max > 1 || product.cnt > 1 ? (
                                    <Stepper
                                      cnt={doComboMobx.doComboResult.gpsNoToCntMap[key]}
                                      min={0}
                                      max={max}
                                      theme='primary'
                                      size='small'
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(isAdd, e) => handleChangeCnt(e, isAdd, group, product)}
                                    />
                                  ) : <i className='iconfont icon-checked check' data-cypid={`aboardOptionalGroup_${product?.productNo}`} onClick={(e: any) => handleCheck(e, group, product)}></i>
                                ) : (
                                  <i className={`iconfont icon-weixuanzhong2x check ${disableTogo ? 'disabled' : ''}`} data-cypid={`aboardOptionalGroup_${product?.productNo}`} onClick={(e: any) => !disableTogo && handleCheck(e, group, product)}></i>
                                )}
                                {product.addPrice > 0 && <div className='add-price'>+{toPrice(product.addPrice)}</div>}
                              </>
                            )
                          }
                          {soldout && <span className='sold-out-msg'>{i18n.t('common.soldout')}</span>}
                        </div>
                      </div>
                    )
                  })
                }
                {!!showSoldout && (
                  <SoldoutFoldBar
                    cnt={soldoutCnt}
                    className='combo-product-item'
                    show={menuMobx.foldSoldoutMap?.[soldoutKey] === true}
                    no={soldoutKey}
                    from='combo'
                  />
                )}
              </div>
            )
          })
        }
      </div>
    </>
  )
}

export default observer(AboardOptionalGroup)
